<template>
  <div class="musubi-page musubi-page--usage-plans">
    <header class="musubi-page__head">
      <h1>Usage &amp; plans</h1>
      <p class="text-muted">
        The plan catalogue (quotas + feature flags enforced at every consume point) and every
        tenant's this-period usage, overrides and audit trail. Platform-operations surface —
        changes here affect what a tenant can do right now.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

    <div class="up-tabs" role="tablist">
      <button type="button" class="up-tab" :class="{ 'is-active': tab === 'plans' }" @click="tab = 'plans'">
        Plan catalogue
      </button>
      <button type="button" class="up-tab" :class="{ 'is-active': tab === 'tenants' }" @click="tab = 'tenants'">
        Tenants &amp; usage
      </button>
      <button type="button" class="up-tab" :class="{ 'is-active': tab === 'senders' }" @click="tab = 'senders'">
        Sender domains
      </button>
    </div>

    <!-- ===================== SCREEN A — PLAN CATALOGUE ===================== -->
    <section v-if="tab === 'plans'" class="up-panel">
      <div v-if="loading.plans" class="text-muted small"><CygnusIcon name="spinner" :spin="true" class="me-1" />Loading plans…</div>

      <div v-else class="up-plan-grid">
        <MusubiSectionCard
          v-for="plan in plans"
          :key="plan.plan_key"
          :title="plan.name"
          :subtitle="plan.plan_key"
          class="up-plan-card"
          :class="{ 'up-plan-card--internal': plan.plan_key === 'internal' }"
        >
          <template #actions>
            <span class="up-plan-count">{{ plan.tenant_count }} tenant{{ plan.tenant_count === 1 ? '' : 's' }}</span>
            <button
              v-if="editingKey !== plan.plan_key"
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="startEdit(plan)"
            >
              Edit
            </button>
          </template>

          <p v-if="plan.plan_key === 'internal'" class="up-internal-note">
            Default plan — nothing enforced. Used for our own workspaces so quotas never block us.
          </p>

          <!-- ---- read-only view ---- -->
          <template v-if="editingKey !== plan.plan_key">
            <div class="up-quota-list">
              <div class="up-qh">Quotas</div>
              <div v-for="m in meterOrder" :key="m" class="up-qrow">
                <span class="up-qk">{{ meterLabels[m] }}</span>
                <span class="up-qv" :class="{ 'is-inf': plan.quotas[m] == null }">{{ fmtQuota(plan.quotas[m]) }}</span>
              </div>
            </div>
            <div class="up-feat">
              <div class="up-fh">Crawl types</div>
              <div class="up-fchips">
                <span
                  v-for="t in knownCrawlTypes"
                  :key="t"
                  class="up-fchip"
                  :class="isCrawlTypeOn(plan, t) ? 'is-on' : 'is-off'"
                >
                  {{ isCrawlTypeOn(plan, t) ? '✓' : '🔒' }} {{ crawlTypeLabels[t] }}
                </span>
              </div>
              <div class="up-fh">Capabilities</div>
              <div class="up-fchips">
                <span
                  v-for="f in knownFeatures"
                  :key="f"
                  class="up-fchip"
                  :class="plan.features[f] ? 'is-on' : 'is-off'"
                >
                  {{ plan.features[f] ? '✓' : '' }} {{ featureLabels[f] }}
                </span>
              </div>
            </div>
          </template>

          <!-- ---- edit form ---- -->
          <template v-else>
            <form class="up-edit-form" @submit.prevent="requestSave(plan)">
              <div class="up-qh">Quotas <span class="up-hint">blank = unlimited</span></div>
              <div v-for="m in meterOrder" :key="m" class="up-edit-row">
                <label :for="`q-${plan.plan_key}-${m}`">{{ meterLabels[m] }}</label>
                <input
                  :id="`q-${plan.plan_key}-${m}`"
                  v-model="edit.quotas[m]"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                  placeholder="unlimited"
                >
              </div>

              <div class="up-fh">Crawl types</div>
              <div class="up-fchips">
                <button
                  v-for="t in knownCrawlTypes"
                  :key="t"
                  type="button"
                  class="up-fchip up-fchip--toggle"
                  :class="edit.crawlTypes.includes(t) ? 'is-on' : 'is-off'"
                  @click="toggleEditCrawlType(t)"
                >
                  {{ edit.crawlTypes.includes(t) ? '✓' : '' }} {{ crawlTypeLabels[t] }}
                </button>
              </div>

              <div class="up-fh">Capabilities</div>
              <div class="up-fchips">
                <button
                  v-for="f in knownFeatures"
                  :key="f"
                  type="button"
                  class="up-fchip up-fchip--toggle"
                  :class="edit.features[f] ? 'is-on' : 'is-off'"
                  @click="edit.features[f] = !edit.features[f]"
                >
                  {{ edit.features[f] ? '✓' : '' }} {{ featureLabels[f] }}
                </button>
              </div>

              <div v-if="internalConfirmArmed" class="up-internal-confirm">
                <p>
                  <strong>{{ plan.name }}</strong> is the operator plan — its quotas are normally
                  unlimited so our own workspaces are never blocked. Save the change anyway?
                </p>
                <div class="up-edit-actions">
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="internalConfirmArmed = false">
                    Back
                  </button>
                  <button type="submit" class="btn btn-sm btn-danger" :disabled="busy.savePlan">
                    <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.savePlan" />Yes, save anyway
                  </button>
                </div>
              </div>
              <div v-else class="up-edit-actions">
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="cancelEdit">Cancel</button>
                <button type="submit" class="btn btn-sm btn-primary" :disabled="busy.savePlan">
                  <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.savePlan" />Save changes
                </button>
              </div>
            </form>
          </template>
        </MusubiSectionCard>
      </div>
    </section>

    <!-- ===================== SCREEN B — TENANTS & USAGE ===================== -->
    <section v-else class="up-panel">
      <div class="up-tenants-head">
        <div>
          <strong>Period {{ period }}</strong>
          <span class="text-muted small"> · usage resets on each tenant's billing anchor</span>
        </div>
        <span class="up-spacer"></span>
        <MusubiStatusPill v-if="nearLimitCount" :status="'near_limit'" tone="warning" :label="`${nearLimitCount} near limit`" />
        <MusubiStatusPill v-if="enforcedCount" :status="'enforced'" tone="danger" :label="`${enforcedCount} enforced`" />
      </div>

      <div v-if="loading.tenants" class="text-muted small"><CygnusIcon name="spinner" :spin="true" class="me-1" />Loading tenants…</div>

      <div v-else class="up-tbl-card">
        <table class="up-tnt">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Plan</th>
              <th>Sending</th>
              <th>Usage this period</th>
              <th>Overrides</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tenants" :key="t.id" :class="{ 'is-over': t.enforced }">
              <td>
                <div class="up-tname">{{ t.name }}</div>
                <div class="up-tsub">tenant_{{ t.id }}<template v-if="t.anchor_day"> · anchor {{ t.anchor_day }}{{ ordinal(t.anchor_day) }}</template></div>
              </td>
              <td><span class="up-planlbl">{{ t.plan_name }}</span></td>
              <td>
                <MusubiStatusPill v-if="t.sending" :status="t.sending.state" :tone="sendingTone(t.sending.state)" :label="sendingLabel(t.sending)" />
              </td>
              <td>
                <div v-if="isUnlimitedTenant(t)" class="up-unlimited-cell">Unlimited — no meters enforced</div>
                <div v-else class="up-meters-cell">
                  <MusubiUsageMeter
                    v-for="m in meterOrder"
                    :key="m"
                    :label="meterShortLabels[m]"
                    :used="t.meters[m].used"
                    :quota="t.meters[m].quota"
                  />
                </div>
              </td>
              <td>
                <MusubiStatusPill v-if="t.enforced" status="enforced" tone="danger" label="Enforced" />
                <MusubiStatusPill v-else-if="overrideSummary(t)" status="override" tone="info" :label="overrideSummary(t)" />
                <MusubiStatusPill v-else status="none" tone="neutral" label="None" />
              </td>
              <td><button type="button" class="btn btn-sm btn-outline-secondary" @click="openDrawer(t.id)">Adjust</button></td>
            </tr>
            <tr v-if="!tenants.length">
              <td colspan="6" class="text-muted small text-center py-3">No tenants yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Send safety slice 4: senders whose From domain may not send, in every workspace. -->
    <section v-if="tab === 'senders'" class="up-panel">
      <SenderDomainCheck />
    </section>

    <!-- ===================== SCREEN C — TENANT DRAWER ===================== -->
    <div v-if="drawer" class="up-drawer-overlay" @click.self="closeDrawer">
      <aside class="up-drawer">
        <div class="up-dhead">
          <button type="button" class="up-dx" aria-label="Close" @click="closeDrawer">✕</button>
          <div v-if="drawer.loading" class="text-muted small"><CygnusIcon name="spinner" :spin="true" class="me-1" />Loading…</div>
          <template v-else>
            <div class="up-dt">{{ drawer.data.name }}</div>
            <div class="up-ds">
              tenant_{{ drawer.data.id }} · billing anchor {{ drawer.data.anchor_day }}{{ ordinal(drawer.data.anchor_day) }} · period {{ drawer.data.period }}
            </div>
            <div v-if="drawer.data.enforced" class="up-denforced">
              <MusubiStatusPill status="enforced" tone="danger" :label="enforcedLabel(drawer.data)" />
            </div>
          </template>
        </div>

        <div v-if="!drawer.loading" class="up-dbody">
          <div v-if="drawer.error" class="alert alert-danger py-2 small">{{ drawer.error }}</div>

          <!-- email sending state (send safety slice 2) -->
          <div class="up-dsec">
            <SendingStateControl :tenant-id="drawer.data.id" @state="onSendingState" />
          </div>

          <!-- sending overview and pre-flight (send safety slice 12) -->
          <div class="up-dsec">
            <SendingPanel :tenant-id="drawer.data.id" />
          </div>

          <!-- plan select -->
          <div class="up-dsec">
            <div class="up-dl">Plan</div>
            <select v-model="drawer.planKey" class="form-select form-select-sm">
              <option v-for="p in plans" :key="p.plan_key" :value="p.plan_key">{{ p.name }}</option>
            </select>
            <div class="up-dnote">Changing plan does not reset the billing anniversary.</div>
          </div>

          <!-- per-meter overrides -->
          <div class="up-dsec">
            <div class="up-dl">Quota overrides <span class="up-hint">this period only</span></div>
            <div v-for="m in meterOrder" :key="m" class="up-ovrow" :class="{ 'is-set': drawer.overrideDeltas[m] !== '' }">
              <div>
                <div class="up-ok">{{ meterLabels[m] }}</div>
                <div class="up-obase">base {{ fmtQuota(drawer.data.plan_quotas[m]) }} · used {{ drawer.data.meters[m].used.toLocaleString() }}</div>
              </div>
              <div class="up-ovinput">
                <span class="up-sgn">+</span>
                <input v-model="drawer.overrideDeltas[m]" type="number" placeholder="0">
              </div>
            </div>
          </div>

          <!-- crawl-type override toggles -->
          <div class="up-dsec">
            <div class="up-dl">Crawl-type overrides <span class="up-hint">on top of plan</span></div>
            <div v-for="ct in drawer.data.crawl_types" :key="ct.type" class="up-togglerow">
              <div>
                <div class="up-tt">{{ crawlTypeLabels[ct.type] }}</div>
                <div class="up-td">{{ ct.locked ? `Enabled by ${drawer.data.plan_name} plan` : (isCrawlOverrideOn(ct.type) ? 'Not in plan — granted as override' : 'Not enabled') }}</div>
              </div>
              <button
                type="button"
                class="up-switch"
                :class="{ 'is-on': ct.locked || isCrawlOverrideOn(ct.type), 'is-locked': ct.locked }"
                :disabled="ct.locked"
                @click="toggleCrawlOverride(ct.type)"
              ></button>
            </div>
          </div>

          <!-- Direct Mail for this workspace (send safety slice 11) -->
          <div class="up-dsec">
            <div class="up-dl">Direct Mail <span class="up-hint">this workspace only</span></div>
            <div class="up-togglerow">
              <div>
                <div class="up-tt">Direct Mail</div>
                <div class="up-td">
                  {{ drawer.directMail ? 'On for this workspace' : 'Off for this workspace' }}{{ drawer.data.direct_mail && !drawer.data.direct_mail.overridden ? ` (from the ${drawer.data.plan_name} plan)` : '' }}
                </div>
              </div>
              <button
                type="button"
                class="up-switch"
                :class="{ 'is-on': drawer.directMail }"
                :aria-pressed="drawer.directMail ? 'true' : 'false'"
                aria-label="Direct Mail for this workspace"
                @click="drawer.directMail = !drawer.directMail"
              ></button>
            </div>
          </div>

          <!-- usage events -->
          <div class="up-dsec">
            <div class="up-dl">Usage events <span class="up-hint">latest {{ drawer.data.events.length }} · audit trail</span></div>
            <div v-if="drawer.data.events.length" class="up-evlog">
              <div v-for="e in drawer.data.events" :key="e.id" class="up-evrow">
                <span class="up-ets">{{ fmtEventTime(e.created) }}</span>
                <span class="up-emeter">
                  {{ meterLabels[e.meter] || e.meter }}
                  <span v-if="e.ref" class="up-eref">{{ e.ref }}</span>
                </span>
                <span class="up-edelta" :class="e.delta < 0 ? 'is-neg' : 'is-pos'">{{ e.delta > 0 ? '+' : '' }}{{ e.delta }}</span>
              </div>
            </div>
            <p v-else class="text-muted small">No usage events recorded yet this period.</p>
          </div>
        </div>

        <div v-if="!drawer.loading" class="up-dfoot">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeDrawer">Cancel</button>
          <span class="up-spacer"></span>
          <button type="button" class="btn btn-sm btn-primary" :disabled="drawer.saving" @click="saveDrawer">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="drawer.saving" />Save overrides
          </button>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
/**
 * AdminUsagePlans — the operator "Usage & plans" admin (usability-refocus
 * slice 2, mockup B): plan catalogue (Screen A) + tenants & usage table
 * (Screen B) + per-tenant overrides/events drawer (Screen C).
 *
 * Reads/writes:
 *   GET/PUT /api/v1/admin/usage/plans[/{key}]
 *   GET/PUT /api/v1/admin/usage/tenants[/{id}]
 *
 * Quota override inputs are DELTA-on-base for readability (mockup shows
 * "+1,000"), but the API stores an ABSOLUTE quota (tenant_plans.overrides_json
 * replaces, not adds — see UsageService::quotaFor()). This component converts
 * delta -> absolute on save and absolute -> delta when opening the drawer.
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiSectionCard from "@root/components/ui/MusubiSectionCard.vue";
import MusubiStatusPill from "@root/components/ui/MusubiStatusPill.vue";
import MusubiUsageMeter from "@root/components/ui/MusubiUsageMeter.vue";
import SendingStateControl from "@root/components/SendingStateControl.vue";
import SendingPanel from "@root/components/SendingPanel.vue";
import SenderDomainCheck from "@root/components/SenderDomainCheck.vue";
import { sendingStateTone, sendingStateLabel } from "./util/sendingState";

const METER_ORDER = ['ai_tokens', 'emails', 'active_prospects', 'users', 'sending_domains'];
const METER_LABELS = {
  ai_tokens: 'Enrichment credits', emails: 'Emails / mo', active_prospects: 'Active prospects',
  users: 'Users', sending_domains: 'Sending domains',
};
const METER_SHORT_LABELS = {
  ai_tokens: 'Credits', emails: 'Emails', active_prospects: 'Prospects', users: 'Users', sending_domains: 'Domains',
};
const FEATURE_LABELS = {
  landing_pages: 'Landing pages', api_access: 'API access', white_label: 'White-label', dedicated_ip: 'Dedicated IP',
  direct_mail: 'Direct Mail',
};
const CRAWL_TYPE_LABELS = {
  google_places: 'Google Places', local_search: 'Local search', linkedin: 'LinkedIn', hackernews: 'HackerNews',
  reddit: 'Reddit', site_crawl: 'Site crawl', agency_crawl: 'Network crawl',
  ch_sic: 'Companies House (SIC)',
};

export default {
  name: 'AdminUsagePlans',
  components: { MusubiSectionCard, MusubiStatusPill, MusubiUsageMeter, SendingStateControl, SendingPanel, SenderDomainCheck },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      tab: 'plans',
      plans: [],
      tenants: [],
      period: '',
      knownFeatures: [],
      knownCrawlTypes: [],
      loading: { plans: true, tenants: true },
      busy: { savePlan: false },
      error: '',
      message: '',

      editingKey: null,
      edit: null, // { quotas: {meter: string}, features: {flag: bool}, crawlTypes: [] }
      internalConfirmArmed: false,

      drawer: null, // { tenantId, loading, saving, error, data, planKey, overrideDeltas: {meter: string} }

      meterOrder: METER_ORDER,
      meterLabels: METER_LABELS,
      meterShortLabels: METER_SHORT_LABELS,
      featureLabels: FEATURE_LABELS,
      crawlTypeLabels: CRAWL_TYPE_LABELS,
    };
  },
  computed: {
    nearLimitCount() {
      return this.tenants.filter((t) => !t.enforced && this.meterOrder.some((m) => {
        const meter = t.meters[m];
        return meter.quota !== null && meter.quota > 0 && (meter.used / meter.quota) >= 0.8;
      })).length;
    },
    enforcedCount() {
      return this.tenants.filter((t) => t.enforced).length;
    },
  },
  mounted() {
    this.loadPlans();
    this.loadTenants();
  },
  methods: {
    // ---- loads ------------------------------------------------------------
    async loadPlans() {
      this.loading.plans = true; this.error = '';
      try {
        // useApi()'s response interceptor already unwraps the axios envelope
        // down to the parsed JSON body (see ApiResponseProcessor.processResponse
        // — it returns `data`, not `{data}`), so `this.api.get()` resolves
        // directly to the body. No further `.data` unwrapping here.
        const p = await this.api.get('/api/v1/admin/usage/plans');
        this.plans = p.data || [];
        this.knownFeatures = p.known_features || [];
        this.knownCrawlTypes = p.known_crawl_types || [];
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Failed to load plans.'; }
      this.loading.plans = false;
    },
    async loadTenants() {
      this.loading.tenants = true; this.error = '';
      try {
        const p = await this.api.get('/api/v1/admin/usage/tenants');
        this.tenants = p.data || [];
        this.period = p.period || '';
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Failed to load tenants.'; }
      this.loading.tenants = false;
    },

    // ---- plan catalogue -----------------------------------------------------
    isCrawlTypeOn(plan, type) {
      return Array.isArray(plan.features?.crawl_types) && plan.features.crawl_types.includes(type);
    },
    fmtQuota(v) {
      return v == null ? '∞' : Number(v).toLocaleString();
    },
    startEdit(plan) {
      this.editingKey = plan.plan_key;
      this.internalConfirmArmed = false;
      const quotas = {};
      this.meterOrder.forEach((m) => { quotas[m] = plan.quotas[m] == null ? '' : String(plan.quotas[m]); });
      const features = {};
      this.knownFeatures.forEach((f) => { features[f] = !!plan.features[f]; });
      this.edit = {
        quotas,
        features,
        crawlTypes: Array.isArray(plan.features?.crawl_types) ? [...plan.features.crawl_types] : [],
      };
    },
    cancelEdit() {
      this.editingKey = null;
      this.edit = null;
      this.internalConfirmArmed = false;
    },
    toggleEditCrawlType(type) {
      const i = this.edit.crawlTypes.indexOf(type);
      if (i === -1) this.edit.crawlTypes.push(type);
      else this.edit.crawlTypes.splice(i, 1);
    },
    requestSave(plan) {
      if (plan.plan_key === 'internal' && !this.internalConfirmArmed) {
        this.internalConfirmArmed = true;
        return;
      }
      this.savePlan(plan);
    },
    async savePlan(plan) {
      this.busy.savePlan = true; this.error = ''; this.message = '';
      try {
        const quotas = {};
        this.meterOrder.forEach((m) => {
          const raw = this.edit.quotas[m];
          quotas[m] = (raw === '' || raw === null || raw === undefined) ? null : Math.max(0, parseInt(raw, 10) || 0);
        });
        const features = { ...this.edit.features, crawl_types: this.edit.crawlTypes };
        const res = await this.api.put(`/api/v1/admin/usage/plans/${plan.plan_key}`, { quotas, features });
        const updated = res?.data;
        if (updated) {
          const idx = this.plans.findIndex((p) => p.plan_key === plan.plan_key);
          if (idx !== -1) this.plans.splice(idx, 1, updated);
          this.message = `${updated.name} updated.`;
        }
        this.cancelEdit();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to save plan.';
      }
      this.busy.savePlan = false;
    },

    // ---- tenants & usage -----------------------------------------------------
    isUnlimitedTenant(t) {
      return this.meterOrder.every((m) => t.meters[m].quota === null);
    },
    overrideSummary(t) {
      if (!t.overrides || !t.overrides.length) return '';
      if (t.overrides.length === 1) {
        const o = t.overrides[0];
        if (o.delta !== null) {
          const sign = o.delta >= 0 ? '+' : '';
          return `${sign}${o.delta.toLocaleString()} ${this.meterShortLabels[o.meter].toLowerCase()}`;
        }
        return `${this.meterShortLabels[o.meter]} overridden`;
      }
      return `${t.override_count} overrides`;
    },
    ordinal(n) {
      if (!n) return '';
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    },

    // ---- drawer ---------------------------------------------------------------
    enforcedLabel(data) {
      const hit = this.meterOrder.find((m) => {
        const meter = data.meters[m];
        return meter.quota !== null && meter.used >= meter.quota;
      });
      return hit ? `${this.meterLabels[hit]} enforced — over quota` : 'Over quota — enforced';
    },
    isCrawlOverrideOn(type) {
      return this.drawer?.crawlOverrideExtras?.includes(type) || false;
    },
    toggleCrawlOverride(type) {
      const extras = this.drawer.crawlOverrideExtras;
      const i = extras.indexOf(type);
      if (i === -1) extras.push(type);
      else extras.splice(i, 1);
    },
    async openDrawer(tenantId) {
      this.drawer = { tenantId, loading: true, saving: false, error: '', data: null, planKey: '', overrideDeltas: {}, crawlOverrideExtras: [] };
      try {
        const res = await this.api.get(`/api/v1/admin/usage/tenants/${tenantId}`);
        const data = res?.data;
        const overrideDeltas = {};
        this.meterOrder.forEach((m) => {
          const found = (data.overrides || []).find((o) => o.meter === m);
          if (!found) { overrideDeltas[m] = ''; return; }
          overrideDeltas[m] = found.delta !== null ? String(found.delta) : String(found.override ?? '');
        });
        const planCrawlTypes = (data.crawl_types || []).filter((c) => c.locked).map((c) => c.type);
        const overriddenCrawlTypes = (data.crawl_types || [])
          .filter((c) => !c.locked && c.effective_enabled)
          .map((c) => c.type);
        this.drawer = {
          tenantId, loading: false, saving: false, error: '', data,
          planKey: data.plan_key || '',
          overrideDeltas,
          planCrawlTypes,
          crawlOverrideExtras: overriddenCrawlTypes,
          directMail: !!data?.direct_mail?.enabled,
        };
      } catch (e) {
        this.drawer = { tenantId, loading: false, saving: false, error: e?.response?.data?.error || e?.message || 'Failed to load tenant.', data: null, overrideDeltas: {}, crawlOverrideExtras: [] };
      }
    },
    closeDrawer() {
      this.drawer = null;
    },
    // ---- sending state (the Sending column) ---------------------------------
    sendingTone(state) {
      return sendingStateTone(state);
    },
    sendingLabel(sending) {
      return sendingStateLabel(sending);
    },
    // The drawer's sending control reports the workspace's state each time it
    // loads or changes it; put that on the table row so the Sending column
    // matches the drawer without reloading the list.
    onSendingState({ tenantId, state, reason }) {
      const row = this.tenants.find((t) => t.id === tenantId);
      if (row) row.sending = { state, reason: reason ?? null };
    },
    async saveDrawer() {
      if (!this.drawer?.data) return;
      this.drawer.saving = true; this.drawer.error = '';
      try {
        const overrides = {};
        this.meterOrder.forEach((m) => {
          const raw = this.drawer.overrideDeltas[m];
          if (raw === '' || raw === null || raw === undefined) return;
          const delta = parseInt(raw, 10) || 0;
          const base = this.drawer.data.plan_quotas[m];
          overrides[m] = base === null ? delta : base + delta;
        });
        // Crawl types: only send an override if the effective set differs from
        // the plan's own set (i.e. at least one extra type was granted) — an
        // override identical to the plan is no override at all, so it's
        // omitted and left to inherit naturally.
        const planTypes = this.drawer.planCrawlTypes || [];
        const fullSet = Array.from(new Set([...planTypes, ...this.drawer.crawlOverrideExtras]));
        const sameAsPlan = fullSet.length === planTypes.length && fullSet.every((t) => planTypes.includes(t));
        if (!sameAsPlan) {
          overrides.crawl_types = fullSet;
        }
        // Direct Mail: send the workspace's own setting when it changes or is
        // already its own; otherwise leave it to the plan (the server keeps the
        // stored feature flags when `features` is not sent).
        const dm = this.drawer.data.direct_mail || {};
        if (dm.overridden || this.drawer.directMail !== !!dm.enabled) {
          overrides.features = { ...(this.drawer.data.overrides_detail?.features || {}), direct_mail: this.drawer.directMail };
        }

        const body = { plan_key: this.drawer.planKey, overrides };
        await this.api.put(`/api/v1/admin/usage/tenants/${this.drawer.tenantId}`, body);
        this.message = `${this.drawer.data.name} updated.`;
        const tenantId = this.drawer.tenantId;
        await this.loadTenants();
        await this.openDrawer(tenantId);
      } catch (e) {
        this.drawer.error = e?.response?.data?.error || e?.message || 'Failed to save overrides.';
      }
      if (this.drawer) this.drawer.saving = false;
    },
    fmtEventTime(ts) {
      if (!ts) return '—';
      return new Date(ts * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.musubi-page--usage-plans { padding: var(--m-space-6); }

.up-tabs { display: flex; gap: 4px; border-bottom: 1px solid var(--m-border); margin-bottom: var(--m-space-5); }
.up-tab {
  font: inherit; font-size: 0.85rem; font-weight: 600; color: var(--m-text-tertiary);
  background: none; border: none; border-bottom: 2px solid transparent;
  padding: 10px 4px 9px; margin-right: 18px; cursor: pointer;
}
.up-tab:hover { color: var(--m-text-secondary); }
.up-tab.is-active { color: var(--m-accent); border-bottom-color: var(--m-accent); }

.up-spacer { margin-left: auto; }

/* ---- plan catalogue ---- */
.up-plan-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: var(--m-space-4); align-items: start; }
.up-plan-card :deep(.m-card__head) { align-items: flex-start; }
.up-plan-card--internal :deep(.m-card) { border-color: var(--m-accent); }
.up-plan-count { font-size: 0.72rem; color: var(--m-text-tertiary); white-space: nowrap; }
.up-internal-note { font-size: 0.76rem; color: var(--m-text-tertiary); margin-bottom: 10px; }

.up-qh, .up-fh { font-size: 0.62rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: var(--m-text-tertiary); margin-bottom: 6px; }
.up-fh { margin-top: 12px; }
.up-hint { font-weight: 600; letter-spacing: 0; text-transform: none; margin-left: 6px; }
.up-qrow { display: flex; justify-content: space-between; padding: 4px 0; font-size: 0.8rem; border-top: 1px solid var(--m-bg-content); }
.up-qrow:first-of-type { border-top: none; }
.up-qk { color: var(--m-text-secondary); }
.up-qv { font-weight: 700; font-family: var(--m-font-mono); }
.up-qv.is-inf { color: var(--m-status-success); }

.up-fchips { display: flex; flex-wrap: wrap; gap: 6px; }
.up-fchip {
  display: inline-flex; align-items: center; gap: 4px; font: inherit; font-size: 0.7rem; font-weight: 600;
  border-radius: 7px; padding: 4px 9px; border: 1px solid var(--m-border); background: var(--m-bg-card); color: var(--m-text-secondary);
}
.up-fchip.is-on { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-accent-hover); }
.up-fchip.is-off { color: var(--m-text-tertiary); border-style: dashed; }
.up-fchip--toggle { cursor: pointer; }

.up-edit-form { display: flex; flex-direction: column; }
.up-edit-row { display: grid; grid-template-columns: 1fr 100px; align-items: center; gap: 8px; padding: 3px 0; }
.up-edit-row label { font-size: 0.8rem; color: var(--m-text-secondary); }
.up-edit-actions { display: flex; gap: 8px; margin-top: 14px; }
.up-internal-confirm {
  margin-top: 12px; padding: 10px 12px; border-radius: 8px;
  background: color-mix(in srgb, var(--m-status-warning) 12%, #fff); border: 1px solid var(--m-status-warning);
}
.up-internal-confirm p { font-size: 0.76rem; color: var(--m-text-secondary); margin-bottom: 8px; }

/* ---- tenants table ---- */
.up-tenants-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.up-tbl-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); overflow: hidden; }
table.up-tnt { width: 100%; border-collapse: collapse; font-size: 0.82rem; }
table.up-tnt thead th {
  text-align: left; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase;
  color: var(--m-text-tertiary); padding: 11px 16px; border-bottom: 1px solid var(--m-border); background: var(--m-bg-content); white-space: nowrap;
}
table.up-tnt tbody td { padding: 12px 16px; border-bottom: 1px solid var(--m-bg-content); vertical-align: middle; }
table.up-tnt tbody tr:hover { background: var(--m-bg-content); }
table.up-tnt tbody tr.is-over { background: color-mix(in srgb, var(--m-status-danger) 6%, #fff); }
.up-tname { font-weight: 700; }
.up-tsub { font-size: 0.72rem; color: var(--m-text-tertiary); }
.up-planlbl { font-size: 0.78rem; font-weight: 600; }
.up-meters-cell { display: grid; grid-template-columns: repeat(5, minmax(70px, 1fr)); gap: 10px; min-width: 380px; }
.up-unlimited-cell { color: var(--m-text-tertiary); font-size: 0.78rem; }

/* ---- drawer ---- */
.up-drawer-overlay {
  position: fixed; inset: 0; background: var(--m-bg-overlay); z-index: 1000;
  display: flex; justify-content: flex-end;
}
.up-drawer {
  width: 440px; max-width: 100%; height: 100%; background: var(--m-bg-card);
  box-shadow: -12px 0 34px rgba(15, 23, 42, 0.25); display: flex; flex-direction: column;
}
.up-dhead { padding: 18px 20px 14px; border-bottom: 1px solid var(--m-border); position: relative; }
.up-dx { position: absolute; top: 14px; right: 16px; background: none; border: none; color: var(--m-text-tertiary); font-size: 1.1rem; cursor: pointer; }
.up-dt { font-size: 1.05rem; font-weight: 800; }
.up-ds { font-size: 0.74rem; color: var(--m-text-tertiary); margin-top: 2px; }
.up-denforced { margin-top: 10px; }
.up-dbody { padding: 16px 20px; overflow-y: auto; flex: 1; }
.up-dsec { margin-bottom: 20px; }
.up-dl { font-size: 0.62rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: var(--m-text-tertiary); margin-bottom: 9px; display: flex; align-items: center; gap: 8px; }
.up-dnote { font-size: 0.7rem; color: var(--m-text-tertiary); margin-top: 6px; }

.up-ovrow { display: grid; grid-template-columns: 1fr 120px; gap: 10px; align-items: center; padding: 8px 0; border-top: 1px solid var(--m-bg-content); }
.up-ovrow:first-of-type { border-top: none; }
.up-ok { font-size: 0.8rem; font-weight: 600; color: var(--m-text-secondary); }
.up-obase { font-size: 0.68rem; color: var(--m-text-tertiary); font-family: var(--m-font-mono); }
.up-ovinput { display: flex; align-items: center; border: 1px solid var(--m-border); border-radius: 7px; overflow: hidden; }
.up-ovrow.is-set .up-ovinput { border-color: var(--m-status-success); }
.up-sgn { padding: 0 8px; color: var(--m-status-success); font-weight: 800; font-size: 0.85rem; background: var(--m-bg-content); align-self: stretch; display: flex; align-items: center; }
.up-ovinput input { border: none; outline: none; font: inherit; font-size: 0.82rem; font-family: var(--m-font-mono); padding: 7px 8px; width: 100%; background: transparent; color: var(--m-text-primary); }

.up-togglerow { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 0; border-top: 1px solid var(--m-bg-content); }
.up-togglerow:first-of-type { border-top: none; }
.up-tt { font-size: 0.82rem; font-weight: 600; }
.up-td { font-size: 0.7rem; color: var(--m-text-tertiary); }
.up-switch { width: 38px; height: 22px; border-radius: 999px; position: relative; flex: none; cursor: pointer; background: #CBD5E1; border: none; }
.up-switch.is-on { background: var(--m-status-success); }
.up-switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25); transition: left 0.15s; }
.up-switch.is-on::after { left: 18px; }
.up-switch.is-locked { cursor: not-allowed; opacity: 0.7; }

.up-evlog { border: 1px solid var(--m-border); border-radius: 8px; overflow: hidden; }
.up-evrow { display: grid; grid-template-columns: 96px 1fr auto; gap: 10px; align-items: center; padding: 8px 11px; font-size: 0.75rem; border-top: 1px solid var(--m-bg-content); }
.up-evrow:first-of-type { border-top: none; }
.up-ets { font-family: var(--m-font-mono); color: var(--m-text-tertiary); font-size: 0.68rem; }
.up-emeter { font-weight: 600; color: var(--m-text-secondary); }
.up-eref { font-family: var(--m-font-mono); font-size: 0.66rem; color: var(--m-text-tertiary); display: block; margin-top: 1px; }
.up-edelta { font-family: var(--m-font-mono); font-weight: 700; font-size: 0.74rem; text-align: right; white-space: nowrap; }
.up-edelta.is-neg { color: var(--m-status-danger); }
.up-edelta.is-pos { color: var(--m-status-success); }

.up-dfoot { padding: 14px 20px; border-top: 1px solid var(--m-border); display: flex; gap: 10px; background: var(--m-bg-content); }
</style>
